import axios from "@util/bdxValidationAxios";

// FastAPI's List[str] = Query([]) params expect the same bare key repeated
// (e.g. "?report_periods=2026-07&report_periods=2026-06"), not the "key[]="
// bracket suffix axios@0.21.4's own default paramsSerializer produces for
// array values - which FastAPI/Starlette never recognizes as populating that
// parameter, silently leaving it at its empty default instead of erroring.
// URLSearchParams' native .append() already produces the bare-key-repeated
// form for free, so it's used here instead of pulling in a "qs"-style dependency.
function serializeParams(params) {
	const search = new URLSearchParams();

	Object.entries(params || {}).forEach(([key, value]) => {
		if (value === undefined || value === null || value === "") return;

		if (Array.isArray(value)) {
			value.forEach((item) => {
				if (item !== undefined && item !== null && item !== "") search.append(key, item);
			});
			return;
		}

		search.append(key, value);
	});

	return search.toString();
}

class BdxValidationService {
	async getMyRole({ cancelToken } = {}) {
		return await axios({
			url: "/api/reports/bdx-validation/my-role",
			cancelToken,
		});
	}

	// Cross-filtered by every OTHER currently-applied report filter (self-exclusion server-side -
	// see repository._claims_narrowing_clause) - deliberately no flag/severity (this endpoint
	// doesn't accept them - unlike getFlags/getKpis below, which now do, by later explicit
	// request).
	// flag/severity added by explicit request (2026-09-22) - the Party dropdown's own option
	// list now cross-filters by whatever's currently selected in the Flag/Severity dropdowns too,
	// same as bdx_status/report_periods/etc. already did (see repository._claims_narrowing_clause).
	// dca_selections added 2026-09-23 - the Party dropdown's own sections now cross-filter EACH
	// OTHER too (per-section self-exclusion server-side - see get_party_group_options' own
	// docstring), reversing this endpoint's earlier "Party never narrows itself" design.
	// currentFilterParams() already computed this value for every other call in this file; this
	// was the one caller silently dropping it before this change.
	async getPartyGroups({
		bdx_status,
		claim_status,
		report_periods,
		claim_reference,
		limeiq_key,
		flag,
		severity,
		umr,
		dca_selections,
		cancelToken,
	} = {}) {
		return await axios({
			url: "/api/reports/bdx-validation/party-groups",
			params: {
				bdx_status,
				claim_status,
				report_periods,
				claim_reference,
				limeiq_key,
				flag,
				severity,
				umr,
				dca_selections,
			},
			paramsSerializer: serializeParams,
			cancelToken,
		});
	}

	// Cross-filtered by every OTHER currently-applied report filter, INCLUDING dca_selections -
	// each of the 4 returned lists (tpa/claim_reference/limeiq_key/report_periods) is scoped by
	// every filter EXCEPT its own (self-exclusion, computed server-side - see
	// repository.fetch_filter_options' own docstring), so this call always sends the caller's
	// full current filter state and lets the backend apply the right exclusion per field.
	// flag/severity - see getPartyGroups' own comment just above; Report Period stays exempt from
	// them too, same as every other field here (enforced server-side, not by omitting them here).
	async getFilterOptions({
		bdx_status,
		claim_status,
		report_periods,
		claim_reference,
		limeiq_key,
		flag,
		severity,
		umr,
		dca_selections,
		cancelToken,
	} = {}) {
		return await axios({
			url: "/api/reports/bdx-validation/filter-options",
			params: {
				bdx_status,
				claim_status,
				report_periods,
				claim_reference,
				limeiq_key,
				flag,
				severity,
				umr,
				dca_selections,
			},
			paramsSerializer: serializeParams,
			cancelToken,
		});
	}

	// No auth required server-side (static reference data) - see
	// app/reports/bdx_validation/router.py's own flag-column-map endpoint.
	async getFlagColumnMap({ cancelToken } = {}) {
		return await axios({
			url: "/api/reports/bdx-validation/flag-column-map",
			cancelToken,
		});
	}

	// bdx_status: "Active" | "Inactive" | undefined (tristate "All" -> omitted)
	// claim_status: "open" | "closed" | undefined (tristate "All" -> omitted)
	// report_periods/claim_reference/limeiq_key/flag/severity/dca_selections:
	// arrays, each entry sent as a repeated query param (see serializeParams
	// above). dca_selections entries are "PartyType::GroupName" composite
	// strings, unchanged from the Party dropdown's own selection keys. tpa is
	// deliberately never sent here - it's a single-value param backing a
	// standalone TPA dropdown this UI doesn't have (TPA is one of the Party
	// dropdown's own groups instead, via dca_selections).
	async getReport({
		bdx_status,
		claim_status,
		report_periods,
		claim_reference,
		limeiq_key,
		flag,
		severity,
		umr,
		dca_selections,
		page,
		page_size,
		export: exportAll,
		has_flag: hasFlag,
		cancelToken,
	} = {}) {
		return await axios({
			url: "/api/reports/bdx-validation/report",
			params: {
				bdx_status,
				claim_status,
				report_periods,
				claim_reference,
				limeiq_key,
				flag,
				severity,
				umr,
				dca_selections,
				page,
				page_size,
				...(exportAll ? { export: true } : null),
				...(hasFlag ? { has_flag: true } : null),
			},
			paramsSerializer: serializeParams,
			cancelToken,
		});
	}

	// Independently paginated from getReport (see schemas.ReportResponse's own
	// docstring on the backend for why) - one row per (claim_reference,
	// limeiq_key/UMR), not one row per distinct claim.
	async getAdditionalClaimDetails({
		bdx_status,
		claim_status,
		report_periods,
		claim_reference,
		limeiq_key,
		flag,
		severity,
		umr,
		dca_selections,
		page,
		page_size,
		export: exportAll,
		has_flag: hasFlag,
		cancelToken,
	} = {}) {
		return await axios({
			url: "/api/reports/bdx-validation/additional-claim-details",
			params: {
				bdx_status,
				claim_status,
				report_periods,
				claim_reference,
				limeiq_key,
				flag,
				severity,
				umr,
				dca_selections,
				page,
				page_size,
				...(exportAll ? { export: true } : null),
				...(hasFlag ? { has_flag: true } : null),
			},
			paramsSerializer: serializeParams,
			cancelToken,
		});
	}

	// flag/severity added by explicit request - the KPI tiles and this endpoint's own Critical/
	// Non-Critical flag panels now cross-filter by whatever's currently selected in the Flag/
	// Severity dropdowns, same as every other filter already did. Deliberately diverges from the
	// live Power BI report's own Flag/Severity slicers, which don't actually affect these cards
	// despite looking like they should (see the backend's own GET /kpis docstring) - a later,
	// explicit decision, not a reversion of the "getFlags/getKpis deliberately exclude flag/
	// severity" reasoning getPartyGroups' own comment above still correctly describes for itself.
	async getFlags({
		bdx_status,
		claim_status,
		report_periods,
		claim_reference,
		limeiq_key,
		flag,
		severity,
		umr,
		dca_selections,
		cancelToken,
	} = {}) {
		return await axios({
			url: "/api/reports/bdx-validation/flags",
			params: {
				bdx_status,
				claim_status,
				report_periods,
				claim_reference,
				limeiq_key,
				flag,
				severity,
				umr,
				dca_selections,
			},
			paramsSerializer: serializeParams,
			cancelToken,
		});
	}

	// flag/severity - see getFlags' own comment just above.
	async getKpis({
		bdx_status,
		claim_status,
		report_periods,
		claim_reference,
		limeiq_key,
		flag,
		severity,
		umr,
		dca_selections,
		cancelToken,
	} = {}) {
		return await axios({
			url: "/api/reports/bdx-validation/kpis",
			params: {
				bdx_status,
				claim_status,
				report_periods,
				claim_reference,
				limeiq_key,
				flag,
				severity,
				umr,
				dca_selections,
			},
			paramsSerializer: serializeParams,
			cancelToken,
		});
	}

	// Backs the bell icon's notification badge/dropdown - distinct UMRs
	// (programs) matching the current filters, most commonly used with just
	// report_periods (per the endpoint's own docstring: "which programs are
	// in this Report Period").
	async getUmrs({
		bdx_status,
		claim_status,
		report_periods,
		claim_reference,
		limeiq_key,
		flag,
		severity,
		umr,
		dca_selections,
		cancelToken,
	} = {}) {
		return await axios({
			url: "/api/reports/bdx-validation/umrs",
			params: { bdx_status, claim_status, report_periods, claim_reference, limeiq_key, flag, severity, umr, dca_selections },
			paramsSerializer: serializeParams,
			cancelToken,
		});
	}
}

export default new BdxValidationService();
